import DataTable from "@/components/application/data-table";
import EditCollectionSheet from "@/components/application/edit-collection-sheet";
import ImageWithFallback from "@/components/application/image-with-fallback";
import Layout from "@/components/application/layout";
import NewCollectionSheet from "@/components/application/new-collection-sheet";
import PlanLimitAlert from "@/components/application/plan-limit-alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import useDialog from "@/hooks/use-dialog";
import usePermissions from "@/hooks/use-permissions";
import useTranslations from "@/hooks/use-translations";
import { Collection } from "@/types";
import { ColumnDef } from "@tanstack/react-table";
import { MoreHorizontal } from "lucide-react";
import { ReactNode, useState } from "react";

function useColumns(): ColumnDef<Collection>[] {
  const { t } = useTranslations("admin.collections.index");

  return [
    {
      accessorKey: "name",
      header: t("table.name"),
    },
    {
      accessorKey: "state",
      header: t("table.state"),
      cell({ row }) {
        return <span className="capitalize">{row.getValue("state")}</span>;
      },
    },
    {
      id: "products",
      header: t("table.products"),
      cell({ row }) {
        const collection = row.original;
        return (
          <div className="flex flex-wrap gap-2">
            {collection.products.length === 0 ? (
              <Badge variant="secondary">{t("no_products")}</Badge>
            ) : (
              collection.products.map((product) => (
                <ImageWithFallback
                  key={product.id}
                  srcset={[product.image_url]}
                  alt={product.name}
                  className="size-24 object-contain"
                />
              ))
            )}
          </div>
        );
      },
    },
    {
      id: "actions",
      cell({ row }) {
        const collection = row.original;

        return <CollectionsTableRowActions collection={collection} />;
      },
    },
  ];
}

function CollectionsTableRowActions({
  collection,
}: {
  collection: Collection;
}) {
  const { t } = useTranslations("admin.collections.index");
  const editCollectionDialog = useDialog();

  return (
    <>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-8 w-8 p-0">
            <span className="sr-only">{t("actions.open_menu")}</span>
            <MoreHorizontal />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={editCollectionDialog.open}>
            {t("actions.edit")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <EditCollectionSheet
        collection={collection}
        open={editCollectionDialog.isOpen}
        onOpenChange={editCollectionDialog.setIsOpen}
      />
    </>
  );
}

function CollectionsTable({ collections }: { collections: Collection[] }) {
  const columns = useColumns();
  return <DataTable<Collection> columns={columns} data={collections} />;
}

function CollectionsPage({ collections }: { collections: Collection[] }) {
  const { can } = usePermissions();
  const { t } = useTranslations();
  const [open, changeOpen] = useState<boolean>(false);

  return (
    <div className="flex flex-col gap-6">
      {!can("create_collection") && (
        <PlanLimitAlert
          title={t("shared.plan_limit_alert.collections.title")}
        />
      )}
      <div className="flex items-center justify-between">
        <h2 className="font-semibold text-2xl">
          {t("admin.collections.index.title")}
        </h2>
        <span />
        <NewCollectionSheet open={open} onOpenChange={changeOpen} />
      </div>

      <CollectionsTable collections={collections} />
    </div>
  );
}

CollectionsPage.layout = (page: ReactNode) => <Layout children={page} />;

export default CollectionsPage;
